import React, {PropsWithChildren, useEffect, useRef, useState} from 'react';
import { History } from 'history';
import { Router, Route, Switch } from 'react-router-dom';
import IUser from '../Foundations/Types/IUser';
import Environment from '../Foundations/Enum/Environment';
import appContext from '../Foundations/Contexts/AppContext';
import searchContext from '../Foundations/Contexts/SearchContext';
import {googleMapsApiLoader, initPlacesServices } from '../Foundations/API/GoogleApi';
import GoogleApiKey from '../Foundations/Constants/GoogleApiKey';

// Routes
import HomeRoute from './Home';
import ResultsRoute from './Results';
import SearchRoute from './Search';
import {loopRefStatus} from '../Foundations/API/SearchApi';
import { setUserTokenHeaders } from '../Foundations/API/RequestConfig';
import {ISearchQueue} from '../Foundations/Types/ISearchContext';
import APP_PATH from '../Foundations/Constants/AppPath';
import SearchReadyNotificationModal from '../Components/Modals/SearchReadyNotificationModal';

interface IProps {
    user: IUser;
    history: History;
    env: Environment;
}

const MainRoute: React.FC<IProps> = (props: PropsWithChildren<IProps>) => {
    const { user, env, history } = props;
    const [ googleApiIsReady, setGoogleApiIsReady] = useState<boolean>(false);
    const [placesServiceStatus, setPlacesServiceStatus ] = useState<boolean>(false);
    const [queue, setQueue] = useState<ISearchQueue[]>([]);
    const [finishedQueue, setFinishedQueue] = useState<ISearchQueue | null>(null);
    const googleMapsHidden = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        if (typeof user.token !== 'undefined') {
            setUserTokenHeaders(user.token);
        }
    }, [user.token]);

    useEffect(
        () => {
            // Load google api that will be used in autosuggestion and map
            if (!googleApiIsReady) {
                googleMapsApiLoader(GoogleApiKey[env])
                    .then((result: boolean) => setGoogleApiIsReady(result));
            }

            if (
                !placesServiceStatus && googleApiIsReady &&
                googleMapsHidden !== null
            ) {
                initPlacesServices(googleMapsHidden);
                setPlacesServiceStatus(true);
            }
        },
        [googleApiIsReady, env, placesServiceStatus]
    );

    useEffect(() => {
        const newQueue = [...queue];
        let shouldUpdate: boolean = false;

        newQueue.forEach((q) => {
           if (q.isStarted === false) {
              q.isStarted = true;
              shouldUpdate = true;

              loopRefStatus(env, q, (finisehdQ) => {
                 setQueue([...queue, finisehdQ]);
                 setFinishedQueue(finisehdQ);
              });
           }
        });

        if (shouldUpdate !== false) {
            setQueue(newQueue);
        }

    }, [queue, env])

    if (!googleApiIsReady) {
        return null;
    }

    const handleQueue = (newVal: ISearchQueue) => {
        const newQueue: ISearchQueue[] = [...queue, newVal];
        setQueue(newQueue);
    }

    return (
       <appContext.Provider value={{ user, env }}>
           <searchContext.Provider value={{ setQueue: handleQueue, setFinishedQueue,  queue }}>
               <Router history={ history }>
                   <Switch>
                       <Route exact path={`${APP_PATH}`} component={HomeRoute} />
                       <Route path={`${APP_PATH}search/:referenceId`} component={SearchRoute} />
                       <Route path={`${APP_PATH}result/:referenceId`} component={ResultsRoute} />
                   </Switch>
               </Router>
           </searchContext.Provider>
           {
               finishedQueue !== null &&
               <SearchReadyNotificationModal
                   queue={finishedQueue}
                   hide={() => setFinishedQueue(null)}
                   history={history}
               />
           }
           <div className="hidden" ref={googleMapsHidden} />
       </appContext.Provider>
    )
}

export default MainRoute;
